import CompanyLocation from "./company-location";
import { normalizeNpi } from "../../utils/npi";

// Bidirectional mapping between the FLAT facility form (CompanyAddLocation's
// innerFormGroup: city / phone / hospiceNpi / postal / …) and the NESTED
// CompanyLocationDto the companies API speaks (address{} / contact{phones[]} /
// hospiceInfo{} / palliativeInfo{}).  CO-3159.
//
// Why this exists: Facility.saveData() used to spread the flat form straight into a
// "CompanyLocation" and (with its persistence calls commented out) never send it. The
// two shapes have almost nothing in common, so the mapping has to be explicit — and it
// has to work in BOTH directions, because opening an existing location for edit has to
// flatten the record back into the form.
//
// ⚠️  PUT IS A FULL OVERWRITE. The backend's MapCompanyLocationFromDto copies EVERY
// business field off the request body onto the stored record — including entityType,
// companyId, orgId, isActive, locationId, migratedTenantId, medicareWeek and
// emailDomainFilters. So an update must send a COMPLETE dto rebuilt from the existing
// record with the form's edits laid over it. Sending only the fields the form knows
// about would blank entityType ("LOCATION"), which is exactly what the locations grid
// filters on — the row would vanish from the list. toDto() therefore always takes the
// existing record on edit and preserves everything the form does not own.

/** The flat value CompanyAddLocation's inner form produces / consumes. */
export interface FacilityFormValue {
  name?: string;
  displayCode?: string;
  primaryType?: string;
  website?: string;
  timezone?: string;
  tenantId?: any;
  contact?: string;
  email?: string;
  phone?: string;
  fax?: string;
  federalTaxId?: string;
  hospiceNpi?: string;
  hospiceTaxonomy?: string;
  medicaidProviderId?: string;
  palliativeNpi?: string;
  palliativeTaxonomy?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal?: string;
  postalExtension?: string;
  [key: string]: any;
}

const OFFICE = "OFFICE";
const FAX = "FAX";

function digits(value: any): string {
  return value ? String(value).replace(/\D/g, "") : "";
}

function phoneOfType(location: CompanyLocation, type: string): string {
  const phones: any[] =
    (location && location.contact && (location.contact as any).phones) || [];
  const match = phones.filter((p) => p && p.type === type)[0];
  if (match) return match.number || "";
  // Legacy rows carry an untyped phones[0]; treat it as the office number.
  if (type === OFFICE && phones[0]) return phones[0].number || "";
  return "";
}

/** The service info block the form's NPI / taxonomy / tax-id fields belong to. */
function isPalliative(primaryType: string): boolean {
  return (primaryType || "").toLowerCase() === "palliative";
}

/** NESTED record → FLAT form value (opening an existing location for edit). */
export function toFormValue(location: CompanyLocation): FacilityFormValue {
  if (!location) return {};

  const address: any = location.address || {};
  const contact: any = location.contact || {};
  const hospice: any = location.hospiceInfo || {};
  const palliative: any = location.palliativeInfo || {};

  // zipCode is stored as "75001" or "75001-1234".
  const zip = (address.zipCode || "").split("-");

  const contactName = [contact.firstName, contact.lastName]
    .filter((part) => !!part)
    .join(" ");

  // The form has ONE federal tax id; the record has one per service line. Show the one
  // belonging to the location's primary type.
  const federalTaxId = isPalliative(location.primaryType)
    ? palliative.federalTaxId
    : hospice.federalTaxId;

  return {
    name: location.name || "",
    displayCode: location.displayCode || "",
    primaryType: location.primaryType || "",
    website: location.website || "",
    timezone: location.timezone || "",
    tenantId: location.migratedTenantId || location.orgId || "",
    contact: contactName,
    email: contact.email || "",
    phone: phoneOfType(location, OFFICE),
    fax: phoneOfType(location, FAX),
    federalTaxId: federalTaxId || "",
    hospiceNpi: hospice.npi || "",
    hospiceTaxonomy: hospice.taxonomyCode || "",
    // CO-3392/CO-3393: like federalTaxId, the single Medi-Cal Provider ID
    // field belongs to the primary service line.
    medicaidProviderId: isPalliative(location.primaryType)
      ? palliative.medicaidProviderId || ""
      : hospice.medicaidProviderId || "",
    palliativeNpi: palliative.npi || "",
    palliativeTaxonomy: palliative.taxonomyCode || "",
    address: address.address1 || "",
    address2: address.address2 || "",
    city: address.city || "",
    state: address.state || null,
    country: address.country || null,
    postal: zip[0] || "",
    postalExtension: zip[1] || "",
  };
}

/**
 * FLAT form value → the CompanyLocationDto the API expects.
 *
 * @param form       the facility form's value
 * @param companyId  the company the location belongs to (the URL path param)
 * @param existing   the record being edited, or null/undefined when creating. On edit
 *                   EVERY field the form does not own is carried over from it, because
 *                   the backend PUT overwrites the stored record wholesale.
 */
export function toDto(
  form: FacilityFormValue,
  companyId: string,
  existing?: CompanyLocation
): any {
  const f = form || {};
  const prior: any = existing || {};
  const priorAddress: any = prior.address || {};
  const priorContact: any = prior.contact || {};
  const priorHospice: any = prior.hospiceInfo || {};
  const priorPalliative: any = prior.palliativeInfo || {};

  const nameParts = (f.contact || "").trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ");

  const zipCode = f.postalExtension
    ? `${f.postal || ""}-${f.postalExtension}`
    : f.postal || "";

  const phones: any[] = [];
  if (digits(f.phone)) {
    phones.push({ type: OFFICE, number: digits(f.phone), ext: "" });
  }
  if (digits(f.fax)) {
    phones.push({ type: FAX, number: digits(f.fax), ext: "" });
  }

  const palliativePrimary = isPalliative(f.primaryType);

  // Keep the billing sub-objects the form never shows, so an edit cannot erase them.
  const serviceInfo = (
    priorInfo: any,
    npi: string,
    taxonomy: string,
    taxId: string,
    medicaidProviderId: string
  ) => ({
    // Store TEN digits, always. normalizeNpi() derives and appends the check digit when
    // the user typed only the 9 significant digits, and leaves a correct 10-digit NPI
    // alone. (Before CO-3174 the field was capped at maxlength="9", so the check digit was
    // silently dropped and every NPI saved through this form was corrupt — on an
    // identifier that goes straight onto an 837.) A WRONG 10th digit is never "repaired"
    // here: we cannot know which digit the user fat-fingered, so the validator rejects it
    // instead of storing a plausible-looking, permanently wrong NPI.
    npi: normalizeNpi(npi || ""),
    taxonomyCode: taxonomy || "",
    federalTaxId: taxId || "",
    billingContact: priorInfo.billingContact || {
      firstName: "",
      lastName: "",
      middleInitial: "",
      phoneNumber: "",
    },
    billingAddress: priorInfo.billingAddress || {
      address1: "",
      address2: "",
      city: "",
      country: "",
      county: "",
      state: "",
      zipCode: "",
    },
    ein: priorInfo.ein || "",
    billingStartDate: priorInfo.billingStartDate || "",
    ccn: priorInfo.ccn || "",
    facilityId: priorInfo.facilityId || "",
    // CO-3393 (A.2.5–A.2.7): optional, reference-only — claim generation
    // reads the payer-level secondary_provider_id, never this field.
    medicaidProviderId: medicaidProviderId || "",
  });

  return {
    // ── identity / ownership: the server owns these on create; preserve them on edit ──
    PK: prior.PK || "",
    SK: prior.SK || "",
    locationId: prior.locationId || "",
    companyId: companyId || prior.companyId || "",
    entityType: "LOCATION",
    orgId: prior.orgId || "",
    // isActive is overwritten by the PUT — never let an edit silently deactivate a
    // location. A location being edited is, by definition, still active.
    isActive: existing ? prior.isActive !== false : true,
    isTest: prior.isTest || false,
    isPayrollOnly: prior.isPayrollOnly || false,
    isMigratedRecord: prior.isMigratedRecord || false,
    medicareWeek: prior.medicareWeek || [],
    emailDomainFilters: prior.emailDomainFilters || [],

    // ── the fields the form owns ──
    name: f.name || "",
    displayCode: f.displayCode || "",
    primaryType: f.primaryType || "",
    website: f.website || "",
    timezone: f.timezone || "",
    migratedTenantId: Number(f.tenantId) || 0,
    address: {
      address1: f.address || "",
      address2: f.address2 || "",
      city: f.city || "",
      state: f.state || "",
      country: f.country || "",
      county: priorAddress.county || "",
      zipCode,
    },
    contact: {
      firstName,
      lastName,
      middleInitial: priorContact.middleInitial || "",
      email: f.email || "",
      prefix: priorContact.prefix || "",
      suffix: priorContact.suffix || "",
      phones,
    },
    // The single federalTaxId field belongs to the primary service line; the other
    // line keeps whatever it already had.
    hospiceInfo: serviceInfo(
      priorHospice,
      f.hospiceNpi,
      f.hospiceTaxonomy,
      palliativePrimary ? priorHospice.federalTaxId : f.federalTaxId,
      palliativePrimary
        ? priorHospice.medicaidProviderId
        : f.medicaidProviderId
    ),
    palliativeInfo: serviceInfo(
      priorPalliative,
      f.palliativeNpi,
      f.palliativeTaxonomy,
      palliativePrimary ? f.federalTaxId : priorPalliative.federalTaxId,
      palliativePrimary
        ? f.medicaidProviderId
        : priorPalliative.medicaidProviderId
    ),
  };
}



// WEBPACK FOOTER //
// ./src/models/company-details/company-location-mapper.ts